/* istanbul ignore file */
import { useCallback, useEffect } from 'react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import { Text, Loader, useApplicationContext, FeatureFlags } from '@adc/ui-adc'
import DynamicField from '../dynamicField'
import ButtonSection from '../buttonSection'
import styles from '../styles.scss'
import { StepId } from './types'
import { StepProgressionType, useStepManagerContext } from './stepManager'
import { validateCurrent } from './utils'
import ConversationalFlowLandingPage from './conversationalFlowLandingPage'
import { QuestionType, Step } from '.'

const cx = classnames.bind(styles)

export const StepComponent = ({ currentStep }: { currentStep: Step }) => {
  const { featureFlags } = useApplicationContext()
  const { data } = useStepManagerContext()
  const { isLoading } = data
  const isConversationalFlowLandingPageEnabled = (featureFlags as FeatureFlags)[
    'fe.resi.home.enable-conversational-flow-landing-page'
  ]

  const isLandingPage =
    currentStep.id === StepId.Address && isConversationalFlowLandingPageEnabled

  useEffect(() => {
    if (!isLandingPage) {
      window.scrollTo(0, 0)
    }
  }, [currentStep.id, isLandingPage])

  if (isLandingPage) {
    return (
      <div className={cx('landing-container')}>
        <StepQuestions currentStep={currentStep} featureFlags={featureFlags} />
      </div>
    )
  }

  // For all other cases (flag OFF or non-Address steps), use original structure
  return (
    <>
      <div className={cx('container', 'mobile')}>
        {isLoading ? (
          <Loader active inverted />
        ) : (
          <>
            <StepQuestions
              currentStep={currentStep}
              featureFlags={featureFlags}
            />
            <ButtonSection currentStep={currentStep} />
          </>
        )}
      </div>
    </>
  )
}

export const StepQuestions = ({
  currentStep,
  featureFlags,
}: {
  currentStep: Step
  featureFlags: FeatureFlags
}) => {
  const { data, actions } = useStepManagerContext()
  const { stepProgression, listingId, leadIntake, validations } = data
  const isConversationalFlowLandingPageEnabled =
    featureFlags['fe.resi.home.enable-conversational-flow-landing-page']
  const {
    setLeadIntake,
    setStepProgression,
    setValidations,
    onNextStep,
    onPreviousStep,
  } = actions

  const callbackToValidate = useCallback(
    async (answers: Record<string, string>) => {
      setStepProgression((prev: StepProgressionType) => ({
        ...prev,
        isCurrentlyValidating: true,
      }))

      const { hasErrors, nextValidations } = await validateCurrent(
        answers,
        currentStep.questions
      )
      setValidations((prev: Step) => ({ ...prev, ...nextValidations }))
      setStepProgression((progression: StepProgressionType) => ({
        ...progression,
        isNextDisabled: hasErrors,
        isCurrentlyValidating: false,
      }))
    },
    [currentStep.questions, setStepProgression, setValidations]
  )

  const onFieldBlurCallback = useCallback(
    async (
      value: Record<string, string>,
      validateOnlyOnBlur: boolean | undefined
    ) => {
      const nextAnswers = { ...leadIntake, ...value }
      if (currentStep.questions) {
        currentStep.questions.forEach(q => {
          if (typeof q.disabled === 'function' && q.disabled(nextAnswers)) {
            nextAnswers[q.field] = ''
          }
        })
      }

      if (validateOnlyOnBlur) {
        await callbackToValidate(nextAnswers)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leadIntake, currentStep.questions, callbackToValidate, validations]
  )

  // NOTE: This _badly_ needs a debounce, especially for remote things.
  const onFieldChangeCallback = useCallback(
    async (
      value: Record<string, string>,
      validateOnlyOnBlur: boolean | undefined
    ) => {
      const nextAnswers: Record<string, string> = { ...leadIntake, ...value }
      if (currentStep.questions) {
        currentStep.questions.forEach(q => {
          if (typeof q.disabled === 'function' && q.disabled(nextAnswers)) {
            nextAnswers[q.field] = ''
          }
        })
      }
      if (
        !_.isEqual(nextAnswers, leadIntake) &&
        currentStep.resetFieldsWhenChanged?.length
      ) {
        currentStep.resetFieldsWhenChanged.forEach((fieldName: string) => {
          nextAnswers[fieldName] && delete nextAnswers[fieldName]
        })
      }
      setLeadIntake(nextAnswers)

      if (!validateOnlyOnBlur) {
        await callbackToValidate(nextAnswers)
      }
    },
    [leadIntake, currentStep, setLeadIntake, callbackToValidate]
  )

  // When flag is ON and it's Address step, show ConversationalFlowLandingPage
  if (
    currentStep.id === StepId.Address &&
    currentStep.questions?.[0] &&
    isConversationalFlowLandingPageEnabled
  ) {
    const question = currentStep.questions[0]
    return (
      <ConversationalFlowLandingPage
        question={question}
        onChange={onFieldChangeCallback}
        onBlur={onFieldBlurCallback}
        leadIntake={leadIntake}
        onPreviousStep={onPreviousStep}
        onNextStep={onNextStep}
        error={validations[question.field]}
        stepProgression={stepProgression}
        currentStepId={currentStep.id}
      />
    )
  }

  // For all other cases (flag OFF or non-Address steps), show original questions section
  return (
    <section
      className={cx('questions-section', ...(currentStep.stepStyles || []))}
    >
      <div className={cx('u-mt-3')}>
        {currentStep.title &&
          (typeof currentStep.title === 'function' ? (
            <Text.Header elmId='step-title-text'>
              {currentStep.title(featureFlags)}
            </Text.Header>
          ) : (
            <Text.Header elmId='step-title-text'>
              {currentStep.title}
            </Text.Header>
          ))}

        {[
          StepId.Address,
          StepId.ConfirmAddress,
          StepId.ThankYou,
          StepId.NewContactInformationDesign,
          StepId.PasswordlessSignup,
          StepId.WelcomeBack,
        ].includes(currentStep.id) ? null : (
          <Text className={cx('address', 'u-mb-4')}>
            {leadIntake['__computed__.property.addressDisplayText']}
          </Text>
        )}
      </div>

      {currentStep.subtitle &&
        (typeof currentStep.subtitle === 'function' ? (
          currentStep.subtitle(leadIntake)
        ) : (
          <Text
            size='large'
            className={cx('u-mb-4')}
            elmId='step-subtitle-text'
          >
            {currentStep.subtitle}
          </Text>
        ))}
      {currentStep.questions &&
        currentStep.questions
          .filter(
            question =>
              currentStep.id !== StepId.Address ||
              question.type !== QuestionType.Checkbox ||
              featureFlags['fe.resi.enable-initial-checkboxes-in-conv-flow']
          )
          .map(q => (
            <div
              key={q.field}
              className={cx('dynamic-field', 'ui form', ...(q.styles || []))}
            >
              {/* Render label with asterisk if the question is required.
                          If isRequired is a function, call it with leadIntake, 
                          otherwise use its boolean value. */}
              <Text>
                {typeof q.isRequired === 'function' ? (
                  <>
                    {q.label || ''}{' '}
                    {q.isRequired(leadIntake) && (
                      <span className={cx('asterisk')}>*</span>
                    )}
                  </>
                ) : (
                  <>
                    {q.label || ''}{' '}
                    {q.isRequired && <span className={cx('asterisk')}>*</span>}
                  </>
                )}
              </Text>
              {/* ts-ignore next */}
              <DynamicField
                key={`${q.field}`}
                question={q}
                onChange={onFieldChangeCallback}
                onBlur={onFieldBlurCallback}
                leadIntake={leadIntake}
                onPreviousStep={onPreviousStep}
                onNextStep={onNextStep}
                error={validations[q.field]}
                stepProgression={stepProgression}
                currentStepId={currentStep.id}
              />
            </div>
          ))}

      {currentStep.text ? (
        <div className={cx('u-mt-1', 'u-mr-1', 'u-ml-1')}>
          {currentStep.text(featureFlags, listingId)}
        </div>
      ) : null}
    </section>
  )
}

export default StepComponent
